import { ProductContext } from "~/domains/product/ProductContext";
import { Card } from "~/features/configurator/Linear/components/Card";
import { Product } from "~/domains/product/Product";
import { IconSize } from "~/common/mui/icons/IconSize";
import { NetPrice } from "~/domains/pricing/components/Price/v2/NetPrice";
import { productHasInStock } from "~/domains/product/utils/productDescriptorUtils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { SimpleStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import { ProductComparisonCheckbox } from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { type MouseEvent, useMemo, useState } from "react";
import { Collapse, Stack, styled } from "~/common/mui";
import { useAddProductToWishlistFlow } from "~/features/wishlist/hooks/useAddProductToWishlistFlow";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { useTranslation } from "react-i18next";
import { useSalesHelper } from "~/domains/salesCampaign/utils/salesHelper";
import { SaleTag } from "~/domains/salesCampaign/components/SaleTag/SaleTag";
import { getBothProductPrices } from "~/utils/v2/priceSelectHelper";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { usePurchasePlace } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { renderButtons } from "~/features/saleRecommendations/hooks/useRecommendationTileButtons";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "~/app/store";

const TileRoot = styled("div")({
  position: "relative",
});

const TileCard = styled(Card)(({ theme }) => ({
  minHeight: "100%",
  width: "100%",
  padding: theme.spacing(2),
  paddingBottom: 0,
  transition: "300ms box-shadow ease-in-out",
  background: "white",
  position: "relative",
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  "&:hover": {
    boxShadow:
      "0px 2px 4px -1px rgba(60, 64, 67, 0.2), 0px 1px 20px 4px rgba(60, 64, 67, 0.12), 0px 4px 8px 0px rgba(0, 0, 0, 0.14)",
  },
}));

const OverlayCard = styled("div")(({ theme }) => ({
  background: theme.palette.surface.main,
  zIndex: 1,
  position: "absolute",
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiCard-root": {
    width: "100%",
    transition: "300ms width ease-in-out",
    display: "flex",
    flexFlow: "column",
    gap: theme.spacing(2),
    "&:hover": {
      width: "200%",
    },
  },
}));

const DesignationRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
}));

const TileDesignation = styled(Product.Designation)(({ theme }) => ({
  ...theme.typography.titleLarge,
  fontWeight: "bold",
}));

const TileNetPrice = styled(NetPrice, {
  shouldForwardProp: (prop) => prop !== "$salesPrice",
})<{ $salesPrice: boolean }>(({ theme, $salesPrice }) => ({
  display: "flex",
  flexFlow: "column",
  "& [data-test-id='value']": {
    ...theme.typography.titleLarge,
    color: $salesPrice ? theme.palette.success.main : undefined,
  },
}));

const ExtendedCollapse = styled(Collapse)(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(-2),
  paddingTop: theme.spacing(2),
  whiteSpace: "pre",
}));

interface Props {
  className?: string;
  product: ProductDescriptor;
}

export const PreferredProductTile = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { currentPrice: netPrice, currentPriceType } = getBothProductPrices({
    product: props.product,
    packCode: INVALID_PACKAGE_CODE,
    totalQuantity: 1,
  });
  const inStock = productHasInStock(props.product);

  const [expanded, setExpanded] = useState(false);

  const expand = () => setExpanded(true);
  const shrink = () => setExpanded(false);

  const [addToWishlist] = useAddProductToWishlistFlow({
    productInput: props.product.base,
  });

  const addToWishlistCb = () => addToWishlist();

  const recommendationPlace = usePurchasePlace();
  const recommendationType: PurchaseType = "ppr";

  const onAddToCartClick = useMemo(() => {
    return async (e: MouseEvent<HTMLButtonElement>) => {
      await AddToCartService.openAddToCartWizard(
        {
          productCode: props.product.base.code,
          restrictionQuery: "",
        },
        {
          page: recommendationPlace,
          recommendationPlace,
          recommendationType,
        },
        dispatch,
      );
    };
  }, [dispatch, recommendationPlace, recommendationType, props.product]);

  const showSaleTag = useSalesHelper(props.product).isProductOnSale();

  const buttons = renderButtons(props.product, {
    atc: {
      onClick: onAddToCartClick,
      // @ts-expect-error tracking attributes
      "tracking-aux-ppr-add": "true",
    },
    wishlist: {
      onClick: addToWishlistCb,
      // @ts-expect-error tracking attributes
      "tracking-aux-ppr-wishlist": "true",
      sizeVariant: "side",
    },
  });
  buttons.reverse();

  const renderCard = (canExpand: boolean) => (
    <TileCard
      disableHover={true}
      onMouseEnter={expand}
      onMouseLeave={shrink}
      tracking-aux-ppr-name={props.product.base.designation}
      tracking-aux-ppr-id={props.product.base.code}
    >
      <Product.PdpLink
        tracking-aux-ppr-name={props.product.base.designation}
        tracking-aux-ppr-id={props.product.base.code}
        reco_type={recommendationType}
      >
        <Product.PrimaryImageWithLogos clickable={false} />
      </Product.PdpLink>
      {showSaleTag && (
        <SaleTag sx={{ position: "absolute", top: 0, left: 0 }} size={32} />
      )}
      <DesignationRow>
        <Product.PreferredIndicator iconSize={IconSize.FONT_16} />
        <Product.PdpLink
          tracking-aux-ppr-name={props.product.base.designation}
          tracking-aux-ppr-id={props.product.base.code}
          reco_type={recommendationType}
        >
          <TileDesignation clickable={false} />
        </Product.PdpLink>
      </DesignationRow>
      <TileNetPrice
        price={netPrice}
        extraLabelsEnabled={false}
        $salesPrice={currentPriceType === "salesPrice"}
      />
      <SimpleStockIndicator inStock={inStock} />
      <ExtendedCollapse in={canExpand && expanded} timeout={300}>
        <ProductComparisonCheckbox gaLocation={"preferredProduct"} productCode={props.product.base.code} />
        {buttons.length > 0 && (
          <Stack direction="row" gap={2} sx={{ pb: 2 }}>
            {buttons}
          </Stack>
        )}
      </ExtendedCollapse>
    </TileCard>
  );

  return (
    <ProductContext value={props.product.base}>
      <TileRoot className={props.className}>
        {renderCard(false)}
        <OverlayCard>{renderCard(true)}</OverlayCard>
      </TileRoot>
    </ProductContext>
  );
};
